/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import NoMoreRegister from '../../component/NoMoreRegister';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import TextFieldSelectBox, {
  RTAddressSelectBox,
} from '../../component/SelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {
  PrimaryButton,
  AddButton,
  SecondaryButton,
} from '../../component/BasicButton';
//API import
import {
  getCompositionsGatewayByGatewayID,
  getCompositionsRTUnit,
  postCompositionsRTUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {format, limit, pullDownMenu} from '../../define';

/**
 * 熱源機追加ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const RTAddDialog = (props: {
  /** 熱源機追加ボタンの名前 */
  addButtonName: string;
  /** 熱源機の情報 */
  rtInfo: apiIF.res.getCompositionsRTUnit | null;
  /** 熱源機の情報（set関数） */
  setRTInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsRTUnit | null>
  >;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  //パスパラメータ取得
  const {siteID, gwID, systemType: rtType} = useParams();

  //熱源機データ
  const [unitName, setUnitName] = useState(''); //熱源機の名前
  const [unitAddress, setUnitAddress] = useState(''); //RTアドレス
  //prettier-ignore
  const [unitModel, setUnitModel] = useState<apiIF.reqParameters.postCompositionsRTUnit['unit-model']>('WCMC'); //熱源機モデル
  //prettier-ignore
  const [unitType, setUnitType] = useState<apiIF.reqParameters.postCompositionsRTUnit['unit-type']>('QtonClose'); //熱源機タイプ
  //prettier-ignore
  const [unitPowerSupply, setUnitPowerSupply] = useState<apiIF.reqParameters.postCompositionsRTUnit['power-supply']>('threePhase'); //電源の仕様
  const [unitVoltage, setUnitVoltage] = useState('0'); //入力電圧
  const [unitNumber, setUnitNumber] = useState(0); //熱源機台数
  const [parentSelectionItem, setParentSelectionItem] = useState([
    {
      value: '0',
      label: trans('RTAddDialog.dialogBox.txtField.choices.label.none'),
    },
  ]); //親子室外機設定選択項目
  const [rtParent, setRTParent] = useState('0'); //親子熱源機設定
  //prettier-ignore
  const [nameplate, setNameplate] = useState<apiIF.reqParameters.postCompositionsRTUnit['nameplate']>('ESA30E-25'); //銘版

  //フラグ
  const [isOpenDialog, setIsOpenDialog] = useState(false); //ダイアログ表示フラグ（追加ダイアログ・登録超過ダイアログ）
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ
  const [rtParentDisable, setRTParentDisable] = useState(false); //親機設定項目非表示フラグ

  //TextBoxのエラー状態
  const [unitNameError, setUnitNameError] = useState(false); //熱源機の名前
  const [unitVoltageError, setUnitVoltageError] = useState(false); //入力電圧
  const [serialNumberError, setSerialNumberError] = useState(false); //製造番号
  const [installDateError, setInstallDateError] = useState(false); //納入日
  const [electricPriceError, setElectricPriceError] = useState(false); //電気代単価
  const [co2EmissionFactorError, setCO2EmissionFactorError] = useState(false); //CO2排出係数
  const [unitPriceError1, setUnitPriceError1] = useState(false); //代替燃料単価1
  const [unitPriceError2, setUnitPriceError2] = useState(false); //代替燃料単価2

  //入力項目のチェック
  useEffect(() => {
    if (
      unitName === '' ||
      unitAddress === '' ||
      unitNameError ||
      unitVoltageError ||
      serialNumberError ||
      electricPriceError ||
      co2EmissionFactorError ||
      unitPriceError1 ||
      unitPriceError2 ||
      installDateError ||
      apiWait
    ) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [
    unitName,
    unitAddress,
    unitNameError,
    unitVoltageError,
    serialNumberError,
    electricPriceError,
    co2EmissionFactorError,
    unitPriceError1,
    unitPriceError2,
    installDateError,
    apiWait,
  ]);

  //親子室外機設定選択項目作成
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || gwID === undefined || rtType === undefined) {
      return;
    }

    //熱源機の情報を取得
    getCompositionsRTUnit(siteID, gwID, {
      'max-result': limit.REGISTRATION_NUM_RT_UNIT_MAX * 2,
    }).then(response => {
      //熱源機タイプQ-tonでフィルター
      const parentData = response['unit-informations']
        .filter(rtInfo => {
          return (
            rtInfo['unit-type'] === 'QtonOpen' ||
            rtInfo['unit-type'] === 'QtonClose'
          );
        })
        //RTアドレスを昇順にソート
        .sort((a, b) => {
          return Number(a['address']) - Number(b['address']);
        });

      const tempParentSelectionItem = [
        {
          value: '0',
          label: trans('RTAddDialog.dialogBox.txtField.choices.label.none'),
        },
      ];

      /** 親機として登録されているユニットIDの一覧 */
      const parentList = parentData.map(rtInfo => rtInfo['parent-unit']);
      for (const rtInfo of parentData) {
        if (
          rtInfo['system'] !== rtType.slice(2) ||
          rtInfo['unit-name'] === undefined ||
          rtInfo['parent-unit'] !== '' || //子機の場合
          asParentUseCount(parentList, rtInfo['unit-id']) >= 3 //子が3台以上登録されている場合
        ) {
          continue;
        }
        tempParentSelectionItem.push({
          value: rtInfo['unit-id'],
          label: rtInfo['unit-name'],
        });
      }
      setParentSelectionItem(tempParentSelectionItem);
    });
  }, [props.rtInfo, siteID, gwID, rtType, trans]);

  //登録台数を算出
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || gwID === undefined || rtType === undefined) {
      return;
    }

    //ゲートウェイの情報を取得
    getCompositionsGatewayByGatewayID(siteID, gwID).then(response => {
      if (rtType === 'rt1') {
        setUnitNumber(response['compositions']['rt-system-1'].length);
      } else {
        setUnitNumber(response['compositions']['rt-system-2'].length);
      }
    });
  }, [props.rtInfo, siteID, gwID, rtType]);

  //機種タイプ変更時処理
  useEffect(() => {
    switch (unitType) {
      case 'Hyozan':
        setUnitModel('WLMC');
        setNameplate('HCCV1001E');
        setRTParentDisable(true);
        break;
      case 'QtonOpen':
      case 'QtonClose':
        setUnitModel('WCMC');
        setNameplate('ESA30E-25');
        setRTParentDisable(false);
        break;
      case 'Chiller':
        setUnitModel('B3UM');
        setNameplate('MCUS5001VHE-W');
        setRTParentDisable(true);
        break;
    }
  }, [unitType]);

  //機種モデル変更時処理
  useEffect(() => {
    switch (nameplate) {
      case 'ESA30E-25':
        setUnitModel('WCMC');
        break;
      case 'ESA30EH-25':
        setUnitModel('WCMD');
        break;
      case 'ESA30EH2-25':
        setUnitModel('WCME');
        break;
      case 'HCCV1001E': //fall through
      case 'HCCV1001EA':
        setUnitModel('WLMC');
        break;
      case 'HCCV2001E':
        setUnitModel('WLMF');
        break;
      case 'MCUS5001VHE-W': //fall through
      case 'MCUS5001VHE-W1': //fall through
      case 'MCUS5001VHE-W1A': //fall through
      case 'MCUS5001VHE-W2': //fall through
      case 'MCUS5001VHE-W2A':
        setUnitModel('B3UM');
        break;
    }
  }, [nameplate]);

  /**
   * 熱源機モデル->ポンプモデル変換テーブル(Q-ton用)
   * @param model 熱源機モデル
   * @returns ポンプモデル
   */
  const unitToPumpConvertTable = (
    model: apiIF.reqParameters.postCompositionsRTUnit['unit-model']
  ): apiIF.reqParameters.postCompositionsRTUnit['pump-model'] => {
    switch (model) {
      case 'WCMC':
        return 'PD-51JU2';
      case 'WCMD':
        return 'PD-51JU2';
      case 'WCME':
        return 'PDC88-510C';
      default:
        return undefined;
    }
  };

  /**
   * ADD UNITボタン押下時処理
   */
  const handleClickOpen = () => {
    setIsOpenDialog(true);
  };

  /**
   * OKボタン押下時処理
   */
  const addUnit = () => {
    //パスパラメータを取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      (rtType !== 'rt1' && rtType !== 'rt2')
    ) {
      return;
    }

    setAPIWait(true);

    const addUnitData: apiIF.reqParameters.postCompositionsRTUnit = {
      'unit-name': unitName,
      'system': rtType.slice(2) as '1' | '2',
      'address': unitAddress,
      'unit-type': unitType,
      'unit-model': unitModel,
      'nameplate': nameplate,
      'power-supply': unitPowerSupply,
      /* チラーの場合Textの表示部を400V固定とする。 */
      'input-voltage': unitType === 'Chiller' ? '400' : unitVoltage,
    };
    //熱源機を追加
    switch (unitType) {
      case 'Hyozan':
        break;
      case 'QtonOpen':
      case 'QtonClose':
        addUnitData['pump-model'] = unitToPumpConvertTable(unitModel);
        addUnitData['advanced-setting'] = 'inactive'; //拡張設定有効無効はユニット追加時は無効固定
        addUnitData['parent-unit'] = rtParent === '0' ? '' : rtParent;
        break;
      case 'Chiller':
        addUnitData['capacity'] = '50';
        break;
    }

    postCompositionsRTUnit(siteID, gwID, addUnitData)
      .then(() => {
        //熱源機の情報を取得
        return getCompositionsRTUnit(siteID, gwID, {'max-result': 400});
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['unit-informations'].sort(sortByUnicode('unit-name'));

        props.setRTInfo(response);
        //登録台数を算出
        let num = 0;
        for (const unitInfo of response['unit-informations']) {
          if (unitInfo['system'] === rtType.slice(2)) {
            num++;
          }
        }
        setUnitNumber(num);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
        handleClose();
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setIsOpenDialog(false);
    setUnitName('');
    setUnitVoltage('0');
    setUnitPowerSupply('threePhase');
    setUnitType('QtonClose');
    setUnitModel('WCMC');
    setNameplate('ESA30E-25');
    setRTParent('0');
    setUnitNameError(false);
    setUnitVoltageError(false);
    setSerialNumberError(false);
    setInstallDateError(false);
    setElectricPriceError(false);
    setCO2EmissionFactorError(false);
    setUnitPriceError1(false);
    setUnitPriceError2(false);
  };

  /**
   * 銘版選択セレクトボックス
   * @param unitType 機種タイプ
   * @returns jsx.element
   */
  const UnitModelSelect = (props: {
    unitType: apiIF.reqParameters.postCompositionsRTUnit['unit-type'];
  }) => {
    switch (props.unitType) {
      case 'QtonOpen': // fallthrough
      case 'QtonClose':
        return (
          <TextFieldSelectBox
            label={trans('RTAddDialog.dialogBox.txtField.label.unitModel')}
            choices={pullDownMenu.RT_UNIT_LABEL_QTON}
            setValue={setNameplate}
            value={nameplate}
            id="mv-selectAddRtUnitModel"
            class="gwSetting__contents__selectBox--rtModelQton"
          />
        );
      case 'Hyozan':
        return (
          <TextFieldSelectBox
            label={trans('RTAddDialog.dialogBox.txtField.label.unitModel')}
            choices={pullDownMenu.RT_UNIT_LABEL_HYOZAN}
            setValue={setNameplate}
            value={nameplate}
            id="mv-selectAddRtUnitModel"
            class="gwSetting__contents__selectBox--rtModelHyozan"
          />
        );
      case 'Chiller':
        return (
          <TextFieldSelectBox
            label={trans('RTAddDialog.dialogBox.txtField.label.unitModel')}
            choices={pullDownMenu.RT_UNIT_LABEL_CHILLER}
            setValue={setNameplate}
            value={nameplate}
            id="mv-selectAddRtUnitModel"
            class="gwSetting__contents__selectBox--rtModelChiller"
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      {/*  Q-tonHyozan追加ボタン */}
      <AddButton
        onClick={handleClickOpen}
        id={'mv-btnAddRt'}
        buttonText={props.addButtonName}
      />
      {/* 登録台数が上限に達している場合は、追加付加のメッセージを表示 */}
      {unitNumber >= limit.REGISTRATION_NUM_RT_UNIT_MAX ? (
        <NoMoreRegister
          open={isOpenDialog}
          setOpen={setIsOpenDialog}
          title={trans('NoMoreRegist.errorDlg.title')}
          text={trans('NoMoreRegist.errorDlg.msg')}
        />
      ) : (
        <>
          {/* ダイアログ */}
          <Dialog
            open={isOpenDialog}
            onClose={handleClose}
            aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">
              {trans('RTAddDialog.dialogBox.title')}
            </DialogTitle>
            <DialogContent>
              {/* ダイアログに表示するメッセージ */}
              <DialogContentText>
                {trans('RTAddDialog.dialogBox.msg')}
              </DialogContentText>
              <Stack spacing={1.5}>
                {/* 熱源機名称 */}
                <ChkInputTextbox
                  setValue={setUnitName}
                  setError={setUnitNameError}
                  chkLength={format.BASIC_LENGTH_MAX}
                  type="text"
                  label={trans('RTAddDialog.dialogBox.txtField.label.unitName')}
                  value={unitName}
                  id="mv-inputAddRtName"
                  class="gwSetting__contents__textBox--unitName"
                  helperText=" "
                  errHelperText={trans(
                    'RTAddDialog.dialogBox.txtField.errHelperTxt'
                  )}
                  variant="standard"
                  autoFocus={true}
                  margin="dense"
                  chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
                />
                {/* アドレス */}
                <RTAddressSelectBox
                  setAddress={setUnitAddress}
                  label={trans(
                    'RTAddDialog.dialogBox.txtField.label.remoteTerminalAddress'
                  )}
                  id="mv-selectAddRtUnitAddr"
                  class="gwSetting__contents__textBox--rtAddress"
                />
                {/* 電圧 */}
                <ChkInputTextbox
                  /* チラーは電源電圧400V固定の為、TextBox変更不可とする。 */
                  disabled={unitType === 'Chiller'}
                  setValue={setUnitVoltage}
                  setError={setUnitVoltageError}
                  chkInputTxtOK={format.VOLTAGE_PATTERN_OK}
                  type="text"
                  label={trans('RTAddDialog.dialogBox.txtField.label.voltage')}
                  /* チラーの場合Textの表示部を400V固定とする対応。実際の値はaddUnitDataでセット。 */
                  value={unitType === 'Chiller' ? '400' : unitVoltage}
                  id="mv-inputAddRtVoltage"
                  class="gwSetting__contents__textBox--voltage"
                  helperText=" "
                  errHelperText={trans(
                    'RTAddDialog.dialogBox.txtField.errHelperTxt'
                  )}
                  variant="standard"
                />
                {/* 電源の種類 */}
                <TextFieldSelectBox
                  label={trans(
                    'RTAddDialog.dialogBox.txtField.label.powerSupply'
                  )}
                  choices={pullDownMenu.POWER_SUPPLY_TYPE}
                  setValue={setUnitPowerSupply}
                  default={unitPowerSupply}
                  id="mv-selectAddRtPowSply"
                  class="gwSetting__contents__selectBox--powerSupply"
                />
                {/* 熱源機タイプ */}
                <TextFieldSelectBox
                  label={trans('RTAddDialog.dialogBox.txtField.label.unitType')}
                  choices={pullDownMenu.RT_UNIT_TYPE}
                  setValue={setUnitType}
                  default={unitType}
                  id="mv-selectAddRtUnitType"
                  class="gwSetting__contents__selectBox--rtType"
                />
                {/* 熱源機モデル */}
                <UnitModelSelect unitType={unitType} />
                {/* 親熱源機の選択 */}
                {rtParentDisable === false && (
                  <TextFieldSelectBox
                    label={trans(
                      'RTAddDialog.dialogBox.txtField.label.parentOutdoorUnit'
                    )}
                    choices={parentSelectionItem}
                    setValue={setRTParent}
                    default={rtParent}
                    id="mv-rtAddParentRtSelect"
                    class="gwSetting__contents__textBox--unitName"
                  />
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <SecondaryButton
                disabled={apiWait}
                onClick={handleClose}
                buttonText={trans('RTAddDialog.dialogBox.buttonTxt.cancel')}
                id={'mv-btnCancelAddRt'}
              />
              <PrimaryButton
                onClick={addUnit}
                buttonText={trans('RTAddDialog.dialogBox.buttonTxt.ok')}
                id="mv-btnOkAddRt"
                disabled={okBtnDisabled}
                apiWait={apiWait}
              />
            </DialogActions>
          </Dialog>
          <APIErrorMessage
            open={isOpenErrorDialog}
            setOpen={setIsOpenErrorDialog}
            id={'mv-qtonHyozanAddErrDlgBtn'}
          />
        </>
      )}
    </>
  );
};

/**
 * 親機として登録されている数(子機の数)を求める
 * @param parentList 親機として登録されているユニットIDの一覧
 * @param unitID ユニットID
 * @returns 親機として登録されている数
 */
const asParentUseCount = (
  parentList: (string | undefined)[],
  unitID: string
) => {
  return parentList.reduce((result, parentID) => {
    if (parentID === unitID) result++;
    return result;
  }, 0);
};

export default RTAddDialog;
